import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { billingApi } from '../../utils/billingApi';
import { supabase } from '../../lib/supabase';
import { useActiveTenant } from '../../hooks/useActiveTenant';
import { 
  ExclamationTriangleIcon, 
  CreditCardIcon,
  ExclamationCircleIcon 
} from '@heroicons/react/24/outline';

interface BillingGuardProps {
  children: React.ReactNode;
}

const ALLOWED_PATHS = ['/login', '/register', '/a2p-verification', '/billing', '/forgot-password', '/reset-password'];

/**
 * BillingGuard
 * - Enforces subscription requirement
 * - Enforces payment method requirement
 * - Shows low balance warnings
 * - Allows access until subscription end date if cancelled/paused
 * - Admins bypass all checks
 */
const BillingGuard: React.FC<BillingGuardProps> = ({ children }) => {
  const { user } = useAuth();
  const { activeTenantId, loading: tenantLoading, isAdmin: tenantIsAdmin } = useActiveTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [blockReason, setBlockReason] = useState<'subscription' | 'payment_method' | 'past_due' | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const lastCheckedTenantRef = useRef<string | null>(null);

  useEffect(() => {
    const enforceBilling = async () => {
      // No authenticated user: nothing to enforce yet
      if (!user) {
        setHasAccess(false);
        setChecking(false);
        return;
      }
      if (tenantLoading) return;

      // Owner admin accounts can bypass paywall
      const adminEmails = ['info@nexussales.io', 'admin@nexussales.io'];
      const isAdmin = adminEmails.includes(user.email?.toLowerCase() || '');

      if (isAdmin || tenantIsAdmin) {
        setHasAccess(true);
        setChecking(false);
        return;
      }

      const clientId = activeTenantId;
      if (!clientId) {
        setHasAccess(true);
        setChecking(false);
        return;
      }

      // Skip the expensive async check only if we already verified access for this tenant.
      // The billing status doesn't change when the user navigates between pages.
      // Failed states must be rechecked so users can recover immediately after fixing billing.
      if (lastCheckedTenantRef.current === clientId && hasAccess) {
        return;
      }

      setChecking(true);
      setWarningMessage(null);
      setBlockReason(null);

      try {
        const [{ data: billingSettings }, { data: subscriptionRecord }] = await Promise.all([
          supabase
            .from('client_billing_settings')
            .select('*')
            .eq('client_id', clientId)
            .maybeSingle(),
          supabase
            .from('subscriptions')
            .select('status, current_period_end, cancel_at_period_end, created_at')
            .eq('client_id', clientId)
            .eq('subscription_type', 'platform')
            .not('plan_id', 'is', null)
            .order('current_period_end', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        const normalizeStatus = (value: unknown) => String(value || '').trim().toLowerCase();

        const subscription = await billingApi.getSubscription(clientId).catch(() => null);

        const stripeStatus = normalizeStatus(subscription?.status);
        const dbSubscriptionStatus = normalizeStatus(subscriptionRecord?.status);
        const billingSubStatus = normalizeStatus(billingSettings?.subscription_status);
        const subStatus = stripeStatus || dbSubscriptionStatus || billingSubStatus || null;
        const isActiveSubscription = !!subStatus && ['active', 'trialing', 'trial'].includes(subStatus);

        const currentBalance = billingSettings?.current_balance || 0;
        const lowBalance = currentBalance < 5;

        if (isActiveSubscription) {
          setHasAccess(true);
          if (lowBalance) {
            setWarningMessage(`Low balance: $${currentBalance.toFixed(2)}. Add funds to continue sending messages.`);
          }
        } else if (subStatus === 'past_due' || subStatus === 'unpaid' || subStatus === 'incomplete' || subStatus === 'incomplete_expired') {
          setHasAccess(false);
          setBlockReason('past_due');
        } else {
          setHasAccess(false);
          setBlockReason('subscription');
        }

        lastCheckedTenantRef.current = clientId;
      } catch (error) {
        console.error('Billing check error:', error);
        setHasAccess(false);
        setBlockReason('subscription');
      } finally {
        setChecking(false);
      }
    };

    enforceBilling();
  }, [activeTenantId, hasAccess, location.pathname, tenantIsAdmin, tenantLoading, user]);

  // Path-based bypass: allowed routes skip all billing checks
  if (ALLOWED_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }

  // Show loading state only on initial check
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Block: No payment method
  if (!hasAccess && blockReason === 'payment_method') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-6">
              <CreditCardIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Method Required
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              To continue using NexusSales, you must add a valid payment method.
            </p>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200 text-left">
                  Your card will only be charged for actual usage and subscription fees.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Link
                to="/billing"
                className="block w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition"
              >
                Add Payment Method
              </Link>
              
              <button
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/login', { replace: true });
                }}
                className="block w-full py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Block: Past due - redirect to billing subscriptions (no "required" flag)
  if (!hasAccess && blockReason === 'past_due') {
    if (location.pathname === '/billing') {
      return <>{children}</>;
    }
    return <Navigate to="/billing?tab=subscriptions" replace />;
  }

  // Block: No subscription - redirect to billing page
  if (!hasAccess && blockReason === 'subscription') {
    // Allow access to billing page - let it handle the subscription requirement
    if (location.pathname === '/billing') {
      return <>{children}</>;
    }
    return <Navigate to="/billing?tab=subscriptions&required=true" replace />;
  }

  // Show warning banner if there's a warning
  if (hasAccess && warningMessage) {
    const isUrgent = warningMessage.includes('past due') || warningMessage.includes('Low balance');
    return (
      <>
        <div className={`${isUrgent ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'} border-b px-4 py-3`}>
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center">
              <ExclamationTriangleIcon className={`h-5 w-5 ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'} mr-2`} />
              <span className={`text-sm ${isUrgent ? 'text-red-800 dark:text-red-200' : 'text-yellow-800 dark:text-yellow-200'}`}>
                {warningMessage}
              </span>
            </div>
            <Link
              to="/billing"
              className={`text-sm font-medium ${isUrgent ? 'text-red-700 dark:text-red-300 hover:text-red-900' : 'text-yellow-700 dark:text-yellow-300 hover:text-yellow-900'}`}
            >
              Manage Billing →
            </Link>
          </div>
        </div>
        {children}
      </>
    );
  }

  // Full access
  return <>{children}</>;
};

export default BillingGuard;
